import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import curbSideApi from '@api/curbSide/curbSide';
import { Button } from '@components/Button/Button';
import { DriverCard } from '@components/DriverCard/DriverCard';
import Icon from '@components/Icon/Icon';
import { Popup } from '@components/Popup/Popup';
import { TripHeader } from '@components/TripHeader/TripHeader';
import { TripPoint } from '@components/TripPoint/TripPoint';
import { EIcons } from '@enums/icons.enum';
import { ERoutes } from '@enums/routes.enum';
import { EStyles } from '@enums/style.enum';
import { TripStatus } from '@enums/trips.enum';
import type { RootState } from '@store/store';
import { setCurbSideRequested, setShowGoBack } from '@store/trips/tripsSlice';
import { isPickedUp } from '@utils/isPickedUp';
import { isUberDriver } from '@utils/isUberDriver';

import classes from './TripDetailInfo.module.scss';

type Props = {
	isCollapse: boolean;
	onCollapse: () => void;
};

export const TripDetailInfo: React.FC<Props> = ({ isCollapse, onCollapse }) => {
	const tripId = useSelector((state: RootState) => state.trips.currentTrip?.id);
	const stops = useSelector((state: RootState) => state.trips.currentTrip?.stops);
	const status = useSelector((state: RootState) => state.trips.currentTrip?.status);
	const driver = useSelector((state: RootState) => state.trips.currentTrip?.driver?.id);
	const trackingUrl = useSelector((state: RootState) => state.trips.currentTrip?.trackingUrl);
	const rateTripId = useSelector((state: RootState) => state.trips.currentTrip?.rate?.id);
	const isUber = isUberDriver(driver);
	const hasPickedUp = isPickedUp(stops);
	const showCurbSide = !isUber && !hasPickedUp;
	const isCurbSideRequested = useSelector(
		(state: RootState) => tripId !== undefined && state.trips.curbSideRequestedTripIds.includes(tripId),
	);

	const [isCurbSideLoading, setIsCurbSideLoading] = useState(false);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [showInformedPopup, setShowInformedPopup] = useState(false);
	const [showCurbSideError, setShowCurbSideError] = useState(false);

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const onLocateVehicle = () => {
		window.open(trackingUrl, ERoutes.blank, ERoutes.noopener);
	};

	const onCallDispatcher = () => {
		window.location.href = `${ERoutes.tel}${import.meta.env.VITE_DISPATCHER_PHONE_NUMBER}`;
	};

	const onRateTrip = () => {
		dispatch(setShowGoBack(true));
		navigate(ERoutes.rateTrip);
	};

	const onCurbSide = async () => {
		setShowConfirmPopup(false);
		if (tripId === undefined) {
			return;
		}
		setShowCurbSideError(false);
		setIsCurbSideLoading(true);
		try {
			await curbSideApi.notify({ tripId });
			dispatch(setCurbSideRequested(tripId));
			setShowInformedPopup(true);
		} catch (error) {
			// No toast system in this app: show the same Popup used for success, and log details for debugging.
			// The button stays enabled so the crew member can retry.
			setShowCurbSideError(true);
			// eslint-disable-next-line no-console
			console.error('Curb-side request failed', { tripId, error });
		} finally {
			setIsCurbSideLoading(false);
		}
	};

	return (
		<div className={`${classes['container']} ${isCollapse ? classes['isCollapsed'] : EStyles.emptyQuote}`}>
			<button
				className={`${classes['collapseContainer']} ${isCollapse ? classes['modal-isCollapse'] : EStyles.emptyQuote}`}
				onClick={onCollapse}
			>
				<Icon name={EIcons.Back} style={{ transform: 'rotate(270deg)' }} size={16} color="var(--medium)" />
			</button>
			<TripHeader />
			<div className={classes['collapsibleWrapper']}>
				<div className={classes['innerContainer']}>
					<div className={classes['stopsContainer']}>
						{stops?.map((stop) => (
							<TripPoint
								key={stop.id}
								stopType={stop.passengers[0]?.stopType}
								locationName={stop?.address?.displayName}
								locationType={stop?.address?.type}
								date={stop?.date}
							/>
						))}
					</div>
					{driver && <DriverCard />}
				</div>
				<div className={classes['buttonContainer']}>
					{showCurbSide ? (
						<Button
							secondary
							disabled={isCurbSideRequested}
							isLoading={isCurbSideLoading}
							onClick={() => setShowConfirmPopup(true)}
							className={`${classes['actionButton']} ${classes['fullWidthButton']}`}
							style={{ height: 46, minHeight: 46 }}
						>
							{t('tripDetailInfo.curbSide')}
						</Button>
					) : (
						<Button
							secondary
							disabled={!hasPickedUp}
							onClick={onRateTrip}
							className={`${classes['actionButton']} ${classes['fullWidthButton']}`}
							icon={EIcons.Star}
							iconColor={rateTripId ? 'var(--star)' : 'var(--text)'}
							style={{ height: 46, minHeight: 46 }}
						>
							{t('tripDetailInfo.rate')}
						</Button>
					)}
				</div>
				<div className={classes['buttonsContainer']}>
					<Button
						primary
						onClick={onCallDispatcher}
						className={`${classes['actionButton']} ${classes['callButton']}`}
						icon={EIcons.Phone}
						style={{ height: 46, minHeight: 46 }}
					>
						{t('tripDetailInfo.contactDispatcher')}
					</Button>
					{!isUber && (
						<Button
							disabled={!driver || status !== TripStatus.InRoute || !trackingUrl}
							primary
							onClick={onLocateVehicle}
							className={classes['actionButton']}
							icon={EIcons.LocationMark}
							style={{ height: 46, minHeight: 46 }}
						>
							{t('tripDetailInfo.locateVehicle')}
						</Button>
					)}
				</div>
			</div>
			{showConfirmPopup && (
				<Popup
					message={t('curbSidePopup.confirmMessage')}
					buttonLabel={t('curbSidePopup.cancelButton')}
					onClose={() => setShowConfirmPopup(false)}
					confirmLabel={t('curbSidePopup.confirmButton')}
					onConfirm={onCurbSide}
				/>
			)}
			{showInformedPopup && (
				<Popup message={t('curbSidePopup.message')} buttonLabel={t('curbSidePopup.okButton')} onClose={() => setShowInformedPopup(false)} />
			)}
			{showCurbSideError && (
				<Popup
					message={t('curbSidePopup.errorMessage')}
					buttonLabel={t('curbSidePopup.okButton')}
					onClose={() => setShowCurbSideError(false)}
				/>
			)}
		</div>
	);
};
